
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Notification {
  id: string;
  content: string;
  created_at: string;
  actor_id: string;
  type: string;
  is_read: boolean;
  profiles?: {
    username: string;
    avatar_url: string;
  };
}

const Notifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          profiles:actor_id (
            username,
            avatar_url
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.is_read).length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('New notification:', payload);
          // Fetch the profile data for the new notification
          supabase
            .from('profiles')
            .select('username, avatar_url')
            .eq('id', payload.new.actor_id)
            .single()
            .then(({ data: profile }) => {
              const newNotification = {
                ...payload.new,
                profiles: profile,
              } as Notification; // Type assertion to fix TypeScript error
              setNotifications(prev => [newNotification, ...prev]);
              setUnreadCount(prev => prev + 1);
            });
        }
      )
      .subscribe();

    // Initial fetch
    fetchNotifications();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-flux-cyan rounded-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell className="w-6 h-6 text-flux-cyan" />
            {unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 rounded-full text-[10px]"
              >
                {unreadCount}
              </Badge>
            )}
          </div>
          <h1 className="text-2xl font-bold">Notifications</h1>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="space-y-4">
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No notifications yet
          </div>
        ) : (
          notifications.map((notification) => {
            // Determine notification content based on type
            let content = notification.content;
            if (notification.type === 'quix_like') {
              content = `@${notification.profiles?.username} liked your video`;
            }
            
            return (
              <div
                key={notification.id}
                className={`p-4 rounded-lg border border-border transition-colors ${
                  notification.is_read ? 'bg-card hover:bg-accent/50' : 'bg-accent/30 hover:bg-accent/40'
                }`}
              >
                <p className="text-sm text-foreground">{content}</p>
                <time className="text-xs text-muted-foreground">
                  {new Date(notification.created_at).toLocaleString()}
                </time>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Notifications;
